import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardActionArea,
  Typography,
  Link
} from '@mui/material';
import { API_BASE_URL } from '../utils/api';

interface HomepageBannerAdProps {
  onAdClick?: (adId: number) => void;
}

interface Promotion {
  id: number;
  title: string;
  content: string;
  imageUrl?: string;
  linkUrl?: string;
  type: string;
  displayLocation: string;
  isActive: boolean;
  order: number;
}

const HomepageBannerAd: React.FC<HomepageBannerAdProps> = ({ onAdClick }) => {
  const [ads, setAds] = useState<Promotion[]>([]);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/promotions?type=homepage_banner&displayLocation=homepage&isActive=true`
        );
        if (response.ok) {
          const data = await response.json();
          const homepageAds = Array.isArray(data)
            ? data
                .filter((ad: Promotion) => 
                  ad.displayLocation === 'homepage' || ad.type === 'homepage_banner'
                )
                .sort((a: Promotion, b: Promotion) => a.order - b.order)
            : [];
          setAds(homepageAds.slice(0, 1)); // Show only first banner
        }
      } catch (error) {
        console.error('Error fetching homepage banner ads:', error);
      }
    };

    fetchAds();
  }, []);

  if (ads.length === 0) {
    return null;
  }

  const ad = ads[0];

  return (
    <Box sx={{ mb: 4, width: '100%' }}>
      <Card 
        sx={{ 
          width: '100%',
          border: '1px solid #e0e0e0',
          '&:hover': {
            boxShadow: 4
          }
        }}
      >
        <CardActionArea
          component={ad.linkUrl ? Link : 'div'}
          href={ad.linkUrl || undefined}
          target={ad.linkUrl ? '_blank' : undefined}
          rel={ad.linkUrl ? 'noopener noreferrer' : undefined}
          onClick={async () => {
            if (ad.id) {
              try {
                await fetch(`${API_BASE_URL}/api/promotions/${ad.id}/click`, {
                  method: 'POST'
                });
                if (onAdClick) {
                  onAdClick(ad.id);
                }
              } catch (error) {
                console.error('Error tracking ad click:', error);
              }
            }
          }}
        >
          {ad.imageUrl ? (
            <CardMedia
              component="img"
              height="200"
              image={ad.imageUrl}
              alt={ad.title}
              sx={{ objectFit: 'cover', width: '100%' }}
            />
          ) : (
            <Box sx={{ p: 3, bgcolor: 'primary.light', color: 'white' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                {ad.title}
              </Typography>
              {ad.content && (
                <Typography variant="body1">
                  {ad.content}
                </Typography>
              )}
            </Box>
          )}
        </CardActionArea>
      </Card>
    </Box>
  );
};

export default HomepageBannerAd;




